// src/pages/Aufbau.jsx
import { useEffect } from "react";
import Container from "../components/ui/Container";
import Badge from "../components/ui/Badge";
import { CheckCircle2, AlertCircle, PlayCircle, Hammer, Info, Users } from "lucide-react";

const steps = [
    {
        title: "Vorbereitung",
        text: "Der Fangplatz wird sorgfältig freigeschnitten, um einen ungehinderten Zugang und beste Fangergebnisse zu gewährleisten.",
        img: "/images/aufbau/step1.webp",
    },
    {
        title: "Vermessung & Graben",
        text: "Zur Erleichterung wird eine Schnur auf ca. 5,20 m gespannt. Nun entlang der Schnur einen 4 cm tiefen und 20 cm breiten Graben herstellen.",
        img: "/images/aufbau/step2.webp",
    },
    {
        title: "Das Herzstück setzen",
        text: "Am besten beginnen Sie mit dem Mittelrohr (System). Dieses bildet die Basis für die gesamte Fallen-Konstruktion.",
        img: "/images/aufbau/step3.webp",
    },
    {
        title: "Rechte Erweiterung",
        text: "Setzen Sie das jeweils passende Betonrohr auf der rechten Seite des Systems ein.",
        img: "/images/aufbau/step4.webp",
    },
    {
        title: "Linke Erweiterung",
        text: "Anschließend wird das entsprechende Rohr auf der linken Seite passgenau eingesetzt.",
        img: "/images/aufbau/step5.webp",
    },
    {
        title: "Schutzmechanismus",
        text: "Unter dem Bereich der Verschlussklappen muss zwingend ein Blechstreifen untergelegt werden, um die Funktion dauerhaft zu sichern.",
        img: "/images/aufbau/step6.webp",
    },
    {
        title: "Natürlicher Einlauf",
        text: "In die Endröhren ruhig etwas Erde geben (2–3 cm). Der Einlauf sollte für das Raubwild ebenerdig und natürlich wirken.",
        img: "/images/aufbau/step7.webp",
    },
    {
        title: "Geringer Aufwand",
        text: "Einfachste Aufstellung der Falle – der Untergrund muss nicht exakt eben sein, die Falle verzeiht leichte Unebenheiten.",
        img: "/images/aufbau/step8.webp",
    },
    {
        title: "Perfekte Tarnung",
        text: "Nach dem erfolgreichen Einbau kann die Falle je nach Standort mit natürlichen Materialien verblendet werden.",
        img: "/images/aufbau/step9.webp",
    },
];

export default function Aufbau() {
    const videoEmbedUrl = "https://www.youtube.com/embed/eZ7sos29poU";

    useEffect(() => {
        document.title = "Aufbauanleitung – Tuscher Helmut Betonrohrfallen";
    }, []);

    return (
        <div className="relative overflow-hidden min-h-screen font-sans">
            {/* Hintergrundleuchten */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute top-[5%] -left-[10%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px]" />
                <div className="absolute bottom-[10%] -right-[5%] h-[30%] w-[30%] rounded-full bg-blue-500/5 blur-[100px]" />
            </div>

            <Container className="relative py-14 sm:py-20 lg:py-24">
                {/* Header Section */}
                <div className="max-w-3xl mb-16 md:mb-24">
                    <Badge className="mb-4 bg-brand-50 text-brand-600 dark:bg-brand-900/20 border-brand-100/50">Schritt für Schritt</Badge>
                    <h1 className="text-4xl sm:text-6xl font-heading font-black tracking-tight text-slate-900 dark:text-white uppercase leading-[1.1]">
                        Meine <span className="text-brand-600">Anleitung</span>
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                        Meine Betonrohrfalle ist für die Praxis entwickelt: unkompliziert, robust und in kürzester Zeit einsatzbereit. Hier finden Sie alles, was Sie für eine erfolgreiche Aufstellung wissen müssen.
                    </p>
                </div>

                {/* Checklist Cards */}
                <div className="grid gap-6 md:grid-cols-2 mb-20 lg:mb-32">
                    <div className="group relative rounded-[2.5rem] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 shadow-xl shadow-slate-200/50 dark:shadow-none transition-all hover:shadow-2xl">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 mb-6 shadow-inner">
                            <CheckCircle2 className="h-6 w-6" />
                        </div>
                        <h2 className="text-xl font-black font-heading uppercase text-slate-900 dark:text-white mb-4">Im Lieferumfang enthalten</h2>
                        <ul className="space-y-3">
                            {[
                                "Betonrohrfalle (fertig vormontiert)",
                                "2 Verlängerungen & 2 Klappen",
                                "Gewindestangen mit Ringschrauben",
                                "Praxiserprobtes Auslösesystem"
                            ].map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400 font-medium">
                                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="group relative rounded-[2.5rem] border border-slate-200 dark:border-slate-800 bg-slate-950 p-8 shadow-2xl transition-all">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white mb-6 shadow-lg shadow-brand-500/20">
                            <Hammer className="h-6 w-6" />
                        </div>
                        <h2 className="text-xl font-black font-heading uppercase text-white mb-4">Zusätzlich benötigt</h2>
                        <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                            Für die vollständige Installation benötigen Sie lediglich noch:
                        </p>
                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 mb-4">
                            <div className="text-white font-bold text-lg">4 Betonrohre</div>
                            <div className="text-slate-500 text-xs mt-1">Durchmesser Ø 25 cm oder Ø 30 cm</div>
                        </div>
                        <div className="flex items-start gap-2 text-[10px] text-brand-400 italic">
                            <Info className="h-3 w-3 shrink-0" />
                            <span>Tipp: Diese finden Sie kostengünstig bei jedem lokalen Baustoffhändler.</span>
                        </div>
                    </div>
                </div>

                {/* Video Section */}
                <section className="mb-24 lg:mb-40" data-aos="zoom-in">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="h-10 w-10 rounded-full bg-brand-600 flex items-center justify-center text-white shadow-lg">
                            <PlayCircle className="h-5 w-5" />
                        </div>
                        <h2 className="text-2xl font-black font-heading text-slate-900 dark:text-white uppercase tracking-tight">Video-Anleitung</h2>
                    </div>

                    <div className="relative rounded-[3rem] border-8 border-white dark:border-slate-800 shadow-2xl overflow-hidden bg-slate-100 dark:bg-slate-900 aspect-video group">
                        {videoEmbedUrl ? (
                            <iframe
                                title="Aufbau Video"
                                src={videoEmbedUrl}
                                className="h-full w-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <div className="h-full w-full flex items-center justify-center text-slate-400 italic">Video folgt in Kürze</div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                    </div>
                </section>

                {/* Steps Section */}
                <section>
                    <div className="text-center max-w-2xl mx-auto mb-16 lg:mb-24">
                        <h2 className="text-3xl sm:text-5xl font-heading font-black text-slate-900 dark:text-white uppercase">Anleitung</h2>
                        <div className="mt-4 h-1.5 w-24 bg-brand-600 mx-auto rounded-full" />
                        <p className="mt-6 text-slate-500 font-medium italic">
                            In nur 9 Schritten zum fertigen Fangplatz. Am besten gelingt der Aufbau zu zweit.
                        </p>
                    </div>

                    <div className="grid gap-12 lg:gap-20">
                        {steps.map((s, idx) => (
                            <div
                                key={idx}
                                className={`flex flex-col lg:items-center gap-10 lg:gap-20 ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                                data-aos={idx % 2 === 0 ? "fade-right" : "fade-left"}
                            >
                                {/* Text Column */}
                                <div className="flex-1 space-y-6">
                                    <div className="flex items-center gap-5">
                                        <div className="h-16 w-16 rounded-[2rem] bg-brand-600 shadow-xl shadow-brand-500/20 flex items-center justify-center text-3xl font-black text-white italic">
                                            {idx + 1}
                                        </div>
                                        <h3 className="text-2xl sm:text-3xl font-heading font-black text-slate-900 dark:text-white uppercase tracking-tight">
                                            {s.title}
                                        </h3>
                                    </div>
                                    <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed pl-1 md:pl-20 border-l-2 border-slate-100 dark:border-slate-800 lg:border-none">
                                        {s.text}
                                    </p>
                                </div>

                                {/* Image Column */}
                                <div className="flex-1 group">
                                    <div className="relative rounded-[2.5rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]">
                                        {s.img ? (
                                            <img
                                                src={s.img}
                                                alt={s.title}
                                                loading="lazy"
                                                className="w-full h-auto object-cover aspect-[4/3] grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                                            />
                                        ) : (
                                            <div className="aspect-[4/3] w-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 font-bold italic">
                                                Visualisierung folgt
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-tr from-brand-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Final Success Card */}
                    <div className="mt-24 lg:mt-40 rounded-[3rem] bg-emerald-600 p-8 sm:p-14 text-center text-white shadow-2xl relative overflow-hidden group" data-aos="zoom-up">
                        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-white/10 blur-[100px] transition-transform duration-1000 group-hover:scale-150" />
                        <div className="relative z-10 flex flex-col items-center">
                            <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center mb-6">
                                <Users className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-3xl sm:text-4xl font-heading font-black uppercase mb-4">Waidmannsheil!</h3>
                            <p className="max-w-xl text-emerald-50 text-lg sm:text-xl font-medium">
                                Deine Falle ist nun fertig installiert. Bei weiteren Fragen stehe ich dir jederzeit mit Rat und Tat zur Seite.
                            </p>
                            <div className="mt-10 flex flex-wrap justify-center gap-4">
                                <a href="tel:+491634194552" className="rounded-2xl bg-white text-emerald-600 px-8 py-4 font-black transition-all hover:bg-emerald-50 active:scale-95 shadow-xl">
                                    Jetzt anrufen
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </Container>
        </div>
    );
}
